import {
  useOptionalRouteOrganizationId,
  useRouteOrganizationId,
} from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { requireOrganizationId } from '@/lib/organization-scoping'
import { useMutation, useQuery } from '@tanstack/react-query'
import { customerQueryKeys } from '@/lib/services/customers/customers.query'
import { categoryQueryKeys } from '@/lib/services/categories/categories.query'
import { collectionQueryKeys } from '@/lib/services/collections/collections.query'
import { invalidateDesignQueries } from '@/lib/services/designs/designs.query'
import { inventoryQueryKeys } from '@/lib/services/inventory/inventory.keys'
import { productQueryKeys } from '@/lib/services/products/products.query'
import {
  getActiveCustomerImportAttempt,
  getActiveProductImportAttempt,
  getCustomerImportAttempt,
  getProductImportAttempt,
  importCustomerImport,
  importProductImport,
  previewCustomerImport,
  previewProductImport,
  type ImportAttemptScope,
  type ImportOrganizationScope,
} from './imports.api'
import type {
  CustomerImportFileInput,
  CustomerImportInput,
  ImportAttemptStatus,
  ProductImportFileInput,
  ProductImportInput,
} from './imports.types'

/** Attempt query keys' entries; a key can exist before the organization or attempt is known. */
interface ImportAttemptKeyEntries {
  organizationId?: number | undefined
}

interface ImportAttemptIdKeyEntries extends ImportAttemptKeyEntries {
  importAttemptId?: string | null | undefined
}

interface ImportAttemptQueryOptions {
  enabled?: boolean
}

export const importsMutationKeys = {
  previewCustomerImport: ['imports', 'customer-import', 'preview'],
  importCustomerImport: ['imports', 'customer-import', 'import'],
  activeCustomerImportAttempt: ({ organizationId }: ImportAttemptKeyEntries) => [
    'imports',
    'customer-import',
    'active-attempt',
    organizationId,
  ],
  customerImportAttempt: ({ organizationId, importAttemptId }: ImportAttemptIdKeyEntries) => [
    'imports',
    'customer-import',
    'attempt',
    organizationId,
    importAttemptId,
  ],
  previewProductImport: ['imports', 'product-import', 'preview'],
  importProductImport: ['imports', 'product-import', 'import'],
  activeProductImportAttempt: ({ organizationId }: ImportAttemptKeyEntries) => [
    'imports',
    'product-import',
    'active-attempt',
    organizationId,
  ],
  productImportAttempt: ({ organizationId, importAttemptId }: ImportAttemptIdKeyEntries) => [
    'imports',
    'product-import',
    'attempt',
    organizationId,
    importAttemptId,
  ],
}

const customerImportCompletionWatchers = new Map<string, { clear: () => void }>()
const customerImportCompletionPollIntervalMs = 2000

export function invalidateCustomerImportQueries({ organizationId }: ImportOrganizationScope) {
  return queryClient.invalidateQueries({ queryKey: customerQueryKeys.all({ organizationId }) })
}

export function invalidateProductImportCatalogQueries({ organizationId }: ImportOrganizationScope) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: productQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.all({ organizationId }) }),
    invalidateDesignQueries({ organizationId }),
  ])
}

function customerImportCompletionWatcherKey({
  organizationId,
  importAttemptId,
}: ImportAttemptScope) {
  return `${organizationId}:${importAttemptId}`
}

function isRunningImportStatus(status: ImportAttemptStatus) {
  return status === 'queued' || status === 'running'
}

/** Attempt queries run only once they have an attempt id (see their `enabled`). */
function requireImportAttemptId(importAttemptId: string | null | undefined): string {
  if (!importAttemptId) {
    throw new Error('Import attempt queries require an import attempt id.')
  }

  return importAttemptId
}

async function invalidateCustomerImportAttemptQueries({
  organizationId,
  importAttemptId,
}: ImportAttemptScope) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: importsMutationKeys.activeCustomerImportAttempt({ organizationId }),
    }),
    queryClient.invalidateQueries({
      queryKey: importsMutationKeys.customerImportAttempt({ organizationId, importAttemptId }),
    }),
  ])
}

export function watchCustomerImportAttemptCompletion({
  organizationId,
  importAttemptId,
}: ImportAttemptScope) {
  if (!organizationId || !importAttemptId) {
    return
  }

  const watcherKey = customerImportCompletionWatcherKey({ organizationId, importAttemptId })

  if (customerImportCompletionWatchers.has(watcherKey)) {
    return
  }

  let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null

  const clear = () => {
    if (timeoutId) {
      globalThis.clearTimeout(timeoutId)
    }

    customerImportCompletionWatchers.delete(watcherKey)
  }

  const schedule = (delayMs = customerImportCompletionPollIntervalMs) => {
    timeoutId = globalThis.setTimeout(() => {
      void poll()
    }, delayMs)
  }

  const poll = async () => {
    try {
      const attempt = await getCustomerImportAttempt({ organizationId, importAttemptId })

      queryClient.setQueryData(
        importsMutationKeys.customerImportAttempt({ organizationId, importAttemptId }),
        attempt
      )

      if (attempt.status === 'completed') {
        await Promise.all([
          invalidateCustomerImportQueries({ organizationId }),
          invalidateCustomerImportAttemptQueries({ organizationId, importAttemptId }),
        ])
        clear()

        return
      }

      if (!isRunningImportStatus(attempt.status)) {
        await invalidateCustomerImportAttemptQueries({ organizationId, importAttemptId })
        clear()

        return
      }
    } catch {
      // Keep polling through transient network/API errors while the queued import may still finish.
    }

    if (customerImportCompletionWatchers.has(watcherKey)) {
      schedule()
    }
  }

  customerImportCompletionWatchers.set(watcherKey, { clear })
  schedule(0)
}

export function clearCustomerImportCompletionWatchersForTest() {
  customerImportCompletionWatchers.forEach((watcher) => watcher.clear())
}

export function usePreviewCustomerImportMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: importsMutationKeys.previewCustomerImport,
    mutationFn: ({ file, sourceSystem }: CustomerImportFileInput) =>
      previewCustomerImport({ organizationId, file, sourceSystem }),
  })
}

export function useImportCustomerImportMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: importsMutationKeys.importCustomerImport,
    retry: false,
    mutationFn: ({ file, sourceSystem, importAttemptId }: CustomerImportInput) =>
      importCustomerImport({ organizationId, file, sourceSystem, importAttemptId }),
    onSuccess: (attempt) => {
      if (isRunningImportStatus(attempt.status)) {
        watchCustomerImportAttemptCompletion({ organizationId, importAttemptId: attempt.id })

        return
      }

      if (attempt.status === 'completed') {
        void invalidateCustomerImportQueries({ organizationId })
      }
    },
  })
}

export function useActiveCustomerImportAttemptQuery({
  enabled = true,
}: ImportAttemptQueryOptions = {}) {
  const organizationId = useOptionalRouteOrganizationId()

  return useQuery({
    queryKey: importsMutationKeys.activeCustomerImportAttempt({ organizationId }),
    enabled: Boolean(enabled && organizationId),
    queryFn: () =>
      getActiveCustomerImportAttempt({ organizationId: requireOrganizationId(organizationId) }),
    refetchOnMount: 'always',
    retry: false,
  })
}

export function useCustomerImportAttemptQuery(
  importAttemptId: string | null | undefined,
  { enabled = true }: ImportAttemptQueryOptions = {}
) {
  const organizationId = useOptionalRouteOrganizationId()

  return useQuery({
    queryKey: importsMutationKeys.customerImportAttempt({ organizationId, importAttemptId }),
    enabled: Boolean(enabled && organizationId && importAttemptId),
    queryFn: () =>
      getCustomerImportAttempt({
        organizationId: requireOrganizationId(organizationId),
        importAttemptId: requireImportAttemptId(importAttemptId),
      }),
    refetchInterval: (query) => {
      const status = query.state.data?.status

      return status === 'queued' || status === 'running' ? 2000 : false
    },
  })
}

export function usePreviewProductImportMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: importsMutationKeys.previewProductImport,
    retry: false,
    mutationFn: ({ file, sourceSystem, importScope, websiteNavigation }: ProductImportFileInput) =>
      previewProductImport({
        organizationId,
        file,
        sourceSystem,
        importScope,
        websiteNavigation,
      }),
  })
}

export function useImportProductImportMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: importsMutationKeys.importProductImport,
    retry: false,
    mutationFn: ({
      file,
      sourceSystem,
      importScope,
      websiteNavigation,
      importAttemptId,
    }: ProductImportInput) =>
      importProductImport({
        organizationId,
        file,
        sourceSystem,
        importScope,
        websiteNavigation,
        importAttemptId,
      }),
  })
}

export function useActiveProductImportAttemptQuery({
  enabled = true,
}: ImportAttemptQueryOptions = {}) {
  const organizationId = useOptionalRouteOrganizationId()

  return useQuery({
    queryKey: importsMutationKeys.activeProductImportAttempt({ organizationId }),
    enabled: Boolean(enabled && organizationId),
    queryFn: () =>
      getActiveProductImportAttempt({ organizationId: requireOrganizationId(organizationId) }),
    refetchOnMount: 'always',
    retry: false,
  })
}

export function useProductImportAttemptQuery(
  importAttemptId: string | null | undefined,
  { enabled = true }: ImportAttemptQueryOptions = {}
) {
  const organizationId = useOptionalRouteOrganizationId()

  return useQuery({
    queryKey: importsMutationKeys.productImportAttempt({ organizationId, importAttemptId }),
    enabled: Boolean(enabled && organizationId && importAttemptId),
    queryFn: () =>
      getProductImportAttempt({
        organizationId: requireOrganizationId(organizationId),
        importAttemptId: requireImportAttemptId(importAttemptId),
      }),
    refetchInterval: (query) => {
      const status = query.state.data?.status

      return status === 'queued' || status === 'running' ? 2000 : false
    },
  })
}
